import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAccId,
  selectCurrentUser,
  selectUserId,
  selectUsers,
} from '../components/login/loginSlice';
import {
  getBusinessCompanies,
  getEmployeeAccessibleCompanies,
} from '../components/login/loginApi';
import {
  applyAccountSwitch,
  hasMultipleAccounts,
  isDelegatedCompanyAccess,
  resolveBusinessAccountId,
} from '../utils/accountUtils';

const EMPTY_PICKER_USERS = [];

/**
 * Пикер компаний для раздела: свои компании + компании с выданным доступом.
 */
export function useSectionCompanyPicker(section) {
  const dispatch = useDispatch();
  const userId = useSelector(selectUserId);
  const users = useSelector(selectUsers);
  const currentUser = useSelector(selectCurrentUser);
  const accId = useSelector(selectAccId);

  const businessAccountId = useMemo(
    () => resolveBusinessAccountId({ users, currentUser, userId }),
    [users, currentUser, userId]
  );

  const accountOwnerMeta = useMemo(() => {
    const bizId = Number(businessAccountId) || 0;
    const loginRow = (users || []).find(
      (u) =>
        u &&
        !u.is_company_context &&
        !u.is_company_record &&
        (!bizId || Number(u.user_id) === bizId)
    );
    return {
      photo_path:
        loginRow?.photo_path ||
        currentUser?.owner_photo_path ||
        (currentUser && !currentUser.is_company_context
          ? currentUser.photo_path
          : null) ||
        null,
      name:
        loginRow?.company_name ||
        currentUser?.owner_name ||
        (currentUser && !currentUser.is_company_context
          ? currentUser.name
          : null) ||
        null,
    };
  }, [users, currentUser, businessAccountId]);

  const [companyUsers, setCompanyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadCompanies = useCallback(async () => {
    if (!businessAccountId) {
      setCompanyUsers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const [ownedResult, delegatedResult] = await Promise.all([
        getBusinessCompanies(businessAccountId),
        getEmployeeAccessibleCompanies(businessAccountId, section),
      ]);

      const byCompanyId = new Map();

      if (ownedResult.success) {
        (ownedResult.companies || [])
          .filter((c) => c.company_id)
          .forEach((c) => {
            const legacyId =
              c.legacy_user_id != null ? Number(c.legacy_user_id) : null;
            const companyId = Number(c.company_id);
            byCompanyId.set(companyId, {
              user_id: legacyId || companyId,
              company_name: c.name,
              company_id: companyId,
              legacy_user_id: legacyId,
              has_legacy_user: !!legacyId,
              is_company_context: true,
              is_company_record: true,
              business_user_id: businessAccountId,
              accountant_id: null,
              inn: c.inn || null,
              address: c.address || null,
              access_mode: 'owner',
              employee_user_id: null,
              owner_photo_path: accountOwnerMeta.photo_path,
              owner_name: accountOwnerMeta.name,
            });
          });
      }

      if (delegatedResult.success) {
        (delegatedResult.companies || [])
          .filter((c) => c.company_id)
          .forEach((c) => {
            const companyId = Number(c.company_id);
            if (byCompanyId.has(companyId)) return;
            const legacyId =
              c.legacy_user_id != null ? Number(c.legacy_user_id) : null;
            byCompanyId.set(companyId, {
              user_id: legacyId || companyId,
              company_name: c.name,
              company_id: companyId,
              legacy_user_id: legacyId,
              has_legacy_user: !!legacyId,
              is_company_context: true,
              is_company_record: true,
              business_user_id: businessAccountId,
              company_owner_user_id: c.business_user_id != null ? Number(c.business_user_id) : null,
              accountant_id: c.accountant_id || null,
              inn: c.inn || null,
              address: c.address || null,
              access_mode: 'delegated',
              employee_user_id: businessAccountId,
              access_documents: !!c.access_documents,
              access_invoices: !!c.access_invoices,
              access_bank: !!c.access_bank,
              access_tax: !!c.access_tax,
              access_salary: !!c.access_salary,
              access_buh_bill: !!c.access_buh_bill,
              owner_photo_path: c.owner_photo_path || null,
              owner_name: c.owner_name || null,
            });
          });
      }

      const merged = [...byCompanyId.values()].sort((a, b) =>
        String(a.company_name || '').localeCompare(String(b.company_name || ''), 'ru')
      );

      if (!ownedResult.success && !delegatedResult.success) {
        setCompanyUsers([]);
        setLoadError(
          ownedResult.error ||
            delegatedResult.error ||
            'Не удалось загрузить компании'
        );
        return;
      }

      setCompanyUsers(merged);
      if (!merged.length && (ownedResult.error || delegatedResult.error)) {
        setLoadError(ownedResult.error || delegatedResult.error || '');
      }
    } catch (e) {
      console.error(e);
      setCompanyUsers([]);
      setLoadError('Не удалось загрузить компании');
    } finally {
      setLoading(false);
    }
  }, [businessAccountId, section, accountOwnerMeta.photo_path, accountOwnerMeta.name]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const pickerUsers =
    companyUsers.length > 0 ? companyUsers : users || EMPTY_PICKER_USERS;
  // Список компаний показываем всегда, даже из одной: иначе пикер закрывается
  // без выбора и разделы Документы / Задачи / Чат остаются пустыми.
  const multiple =
    companyUsers.length > 0
      ? companyUsers.length >= 1
      : hasMultipleAccounts(pickerUsers);
  const noAccess = !loading && !loadError && companyUsers.length === 0;

  const isDelegatedAccess = isDelegatedCompanyAccess(currentUser);
  const isEmployee = isDelegatedAccess;

  const [pickerOpen, setPickerOpen] = useState(true);

  useEffect(() => {
    setPickerOpen(true);
  }, [section]);

  const selectAccount = useCallback(
    (user) => {
      if (!user) return;
      const delegated = user.access_mode === 'delegated';
      if (delegated) {
        applyAccountSwitch(dispatch, {
          ...user,
          access_mode: 'delegated',
          employee_user_id: businessAccountId,
          business_user_id: businessAccountId,
          company_owner_user_id: user.company_owner_user_id || null,
          is_company_context: true,
          access_documents: user.access_documents,
          access_invoices: user.access_invoices,
          access_bank: user.access_bank,
          access_tax: user.access_tax,
          access_salary: user.access_salary,
          access_buh_bill: user.access_buh_bill,
        });
      } else {
        applyAccountSwitch(dispatch, {
          ...user,
          access_mode: 'owner',
          is_company_context: true,
          accountant_id:
            user.accountant_id ||
            Number(accId) ||
            currentUser?.accountant_id ||
            null,
          business_user_id: businessAccountId || user.business_user_id,
        });
      }
      setPickerOpen(false);
    },
    [dispatch, accId, currentUser, businessAccountId]
  );

  const openPicker = useCallback(() => {
    if (multiple) {
      setPickerOpen(true);
    }
  }, [multiple]);

  const closePicker = useCallback(() => {
    setPickerOpen(false);
  }, []);

  return {
    isEmployee,
    isDelegatedAccess,
    multiple,
    pickerOpen,
    setPickerOpen,
    selectAccount,
    openPicker,
    closePicker,
    pickerUsers,
    loading,
    loadError,
    noAccess,
    businessAccountId,
    reloadAccessible: loadCompanies,
  };
}
